/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @atlassian/relay/graphql-naming */
import { useMemo } from 'react';
import { css, jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import type { ProjectsList$key } from '@atlassian/jira-relay/src/__generated__/ProjectsList.graphql';
import type { ProjectsList130BadPattern$key } from '@atlassian/jira-relay/src/__generated__/ProjectsList130BadPattern.graphql';
import { Pagination } from './pagination/Pagination.tsx';
import { ResultsTable } from './results-table/ResultsTable.tsx';

export type Props = {
	projectsRef: ProjectsList$key;
	rootQueryRef: ProjectsList130BadPattern$key;
	isLoading?: boolean;
	isAdminSettingsContext: boolean;
};

export const ProjectsList = ({
	projectsRef,
	rootQueryRef,
	isLoading,
	isAdminSettingsContext,
}: Props): JSX.Element => {
	const dataRef = useFragment<ProjectsList$key>(
		graphql`
			fragment ProjectsList on JiraProjectConnection
			@argumentDefinitions(isAdminSettingsContext: { type: "Boolean!" }) {
				...ResultsTable @arguments(isAdminSettingsContext: $isAdminSettingsContext)
				...PaginationPagination
			}
		`,
		projectsRef,
	);

	/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
	const categoriesData = useFragment<ProjectsList130BadPattern$key>(
		graphql`
			fragment ProjectsList130BadPattern on Query
			@argumentDefinitions(isAnonymous: { type: "Boolean!" }) {
				jira {
					allJiraProjectCategories(cloudId: $cloudId) @skip(if: $isAnonymous) {
						edges {
							__typename
						}
					}
					...ActionsCell900BadPattern @arguments(cloudId: $cloudId)
				}
			}
		`,
		rootQueryRef,
	);

	const shouldShowCategories = useMemo(
		() => Boolean(categoriesData.jira?.allJiraProjectCategories?.edges?.length),
		[categoriesData],
	);

	return (
		<div
			data-testid="global-pages.directories.projects-directory-v3.ui.page-layout.projects-list.container"
			css={containerStyles}
		>
			<ResultsTable
				shouldShowCategories={shouldShowCategories}
				projectsRef={dataRef}
				jiraQueryRef={categoriesData.jira || null}
				isAdminSettingsContext={isAdminSettingsContext}
			/>
			<Pagination projectsRef={dataRef} />
			{isLoading && (
				<div css={loadingOverlayStyles}>
					<Spinner
						testId="global-pages.directories.projects-directory-v3.ui.page-layout.projects-list.spinner"
						size="large"
						interactionName="projects-list-loading"
					/>
				</div>
			)}
		</div>
	);
};

const containerStyles = css({
	position: 'relative',
	paddingBottom: token('space.200'),
});

const loadingOverlayStyles = css({
	content: '',
	position: 'absolute',
	display: 'flex',
	justifyContent: 'center',
	inset: 0,
	paddingBlockStart: token('space.1000'),
	'&::before': {
		content: '',
		position: 'absolute',
		inset: 0,
		backgroundColor: token('elevation.surface.overlay'),
		opacity: '0.81',
	},
});
